"use client";
import { twMerge } from "tailwind-merge";
import type { LinkComponentType } from "../../index";
import { Avatar } from "../Avatar";
import type { CellEmphasis } from "../DataTable/DataTable.types";
import { Skeleton } from "../Skeleton";
import { TextLink } from "../TextLink";

export interface ContactMetaDisplayProps {
  /** First name */
  firstName: string;

  /** Last name */
  lastName: string;

  /** Email address (optional) */
  email?: string;

  /** Avatar image URL (optional) */
  avatarUrl?: string;

  /** Layout variant - stacked (vertical) or inline (horizontal) */
  layout?: "stacked" | "inline";

  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg";

  /** Link to contact detail page (optional) */
  href?: string;

  /** Custom link component (e.g., Next.js Link) */
  LinkComponent?: LinkComponentType;

  /** Link variant for the name */
  linkVariant?: "primary" | "secondary" | "muted" | "unstyled" | "default" | "brand";

  /** Text emphasis level - "strong"/"high" for bold, "normal" for regular, "low" for muted */
  emphasis?: CellEmphasis;

  /** Show email address (only for stacked layout) */
  showEmail?: boolean;

  /** Badge element to display next to the name (e.g., Owner, Admin badge) */
  badge?: React.ReactNode;

  /** Avatar shape (default: "square") */
  avatarShape?: "circle" | "square";

  /** Loading state */
  isLoading?: boolean;

  /** Additional classes */
  className?: string;

  /**
   * Entity identifier for PII masking (e.g., "contact:ctc_abc123")
   * When provided, adds data-pii-* attributes for the Curtain extension
   */
  piiEntity?: string;

  /** PII type of the primary label when an email stands in for a missing name. */
  piiPrimaryTextType?: "full-name" | "email";
}

/**
 * ContactMetaDisplay
 *
 * Reusable component for displaying contact information with avatar.
 * Supports stacked and inline layouts with size variants and optional linking.
 *
 * **Stacked layout**: Avatar, name above, optional email below
 * **Inline layout**: Avatar + name only (horizontal)
 *
 * Use this component directly in cards, lists, or other layouts.
 * For DataTable cells, use ContactMetaCell which wraps this component.
 *
 * @example
 * ```tsx
 * // Stacked layout (default)
 * <ContactMetaDisplay
 *   firstName="John"
 *   lastName="Doe"
 *   email="john@example.com"
 *   layout="stacked"
 *   size="md"
 *   showEmail
 *   href="/contacts/123"
 * />
 *
 * // Inline layout
 * <ContactMetaDisplay
 *   firstName="John"
 *   lastName="Doe"
 *   layout="inline"
 *   size="sm"
 *   href="/contacts/123"
 * />
 * ```
 */
export function ContactMetaDisplay({
  firstName,
  lastName,
  email,
  avatarUrl,
  layout = "stacked",
  size = "md",
  href,
  LinkComponent,
  linkVariant = "unstyled",
  emphasis = "normal",
  showEmail = false,
  badge,
  avatarShape,
  isLoading = false,
  className,
  piiEntity,
  piiPrimaryTextType = "full-name",
}: ContactMetaDisplayProps) {
  const effectiveAvatarShape = avatarShape ?? (layout === "inline" ? "circle" : "square");

  // Size-based styling
  const sizes = {
    xs: {
      avatarSize: "xs" as const,
      gap: "gap-1.5",
      textSize: "text-xs",
      secondaryTextSize: "text-xs",
      skeletonAvatar: 24,
      skeletonText: 80,
      skeletonSecondary: 60,
    },
    sm: {
      avatarSize: "sm" as const,
      gap: "gap-2",
      textSize: "text-sm",
      secondaryTextSize: "text-xs",
      skeletonAvatar: 32,
      skeletonText: 96,
      skeletonSecondary: 72,
    },
    md: {
      avatarSize: "md" as const,
      gap: "gap-3",
      textSize: "text-base",
      secondaryTextSize: "text-sm",
      skeletonAvatar: 40,
      skeletonText: 128,
      skeletonSecondary: 96,
    },
    lg: {
      avatarSize: "lg" as const,
      gap: "gap-3",
      textSize: "text-lg",
      secondaryTextSize: "text-base",
      skeletonAvatar: 48,
      skeletonText: 160,
      skeletonSecondary: 120,
    },
  };

  const sizeConfig = sizes[size];
  const fullName = `${firstName} ${lastName}`.trim();

  // Map emphasis to font weight and color
  const emphasisClasses = {
    strong: "font-semibold text-text-primary",
    high: "font-semibold text-text-primary",
    normal: "text-text-primary",
    low: "text-text-secondary",
  };
  const emphasisClass = emphasisClasses[emphasis];

  // Render link wrapper
  const LinkWrapper = ({ children, className: linkClassName }: { children: React.ReactNode; className?: string }) => {
    if (!href) {
      return <span className={linkClassName}>{children}</span>;
    }

    if (LinkComponent) {
      return (
        <LinkComponent href={href} className={linkClassName}>
          {children}
        </LinkComponent>
      );
    }

    return (
      <TextLink
        href={href}
        variant={linkVariant}
        className={twMerge("hover:underline", linkClassName)}
        asButton={false}
      >
        {children}
      </TextLink>
    );
  };

  // Loading state
  if (isLoading) {
    if (layout === "inline") {
      return (
        <div className={twMerge("flex items-center", sizeConfig.gap, className)}>
          <Skeleton
            width={sizeConfig.skeletonAvatar}
            height={sizeConfig.skeletonAvatar}
            className="flex-shrink-0 rounded-full"
          />
          <Skeleton width={sizeConfig.skeletonText} height={sizeConfig.skeletonAvatar * 0.5} />
        </div>
      );
    }

    return (
      <div className={twMerge("flex items-center", sizeConfig.gap, className)}>
        <Skeleton
          width={sizeConfig.skeletonAvatar}
          height={sizeConfig.skeletonAvatar}
          className="flex-shrink-0 rounded-full"
        />
        <div className="flex flex-col gap-1">
          <Skeleton width={sizeConfig.skeletonText} height={16} />
          {showEmail && <Skeleton width={sizeConfig.skeletonSecondary} height={12} />}
        </div>
      </div>
    );
  }

  // Inline layout: Avatar + name only
  if (layout === "inline") {
    return (
      <div className={twMerge("flex items-center", sizeConfig.gap, className)}>
        <LinkWrapper className={twMerge("flex items-center", sizeConfig.gap)}>
          <Avatar
            firstName={firstName}
            lastName={lastName}
            src={avatarUrl}
            size={sizeConfig.avatarSize}
            variant="user"
            shape={effectiveAvatarShape}
            className="flex-shrink-0"
            piiEntity={piiEntity}
          />
          <span
            className={twMerge(
              "leading-normal truncate hover:text-text-primary",
              emphasisClass,
              sizeConfig.textSize,
              href && "hover:underline hover:decoration-1 hover:underline-offset-4"
            )}
            data-pii-type={piiEntity ? piiPrimaryTextType : undefined}
            data-pii-entity={piiEntity}
          >
            {fullName}
          </span>
        </LinkWrapper>
        {badge}
      </div>
    );
  }

  // Stacked layout: Avatar, name above, optional email below
  return (
    <div className={twMerge("flex items-center justify-start", sizeConfig.gap, className)}>
      <Avatar
        firstName={firstName}
        lastName={lastName}
        src={avatarUrl}
        size={sizeConfig.avatarSize}
        variant="user"
        shape={effectiveAvatarShape}
        className="flex-shrink-0"
        piiEntity={piiEntity}
      />
      <div className="flex flex-col items-start min-w-0">
        <div className="flex items-center gap-2">
          <LinkWrapper
            className={twMerge(
              "leading-normal truncate hover:text-text-primary",
              emphasisClass,
              sizeConfig.textSize,
              href && "hover:underline hover:decoration-1 hover:underline-offset-4"
            )}
          >
            <span data-pii-type={piiEntity ? piiPrimaryTextType : undefined} data-pii-entity={piiEntity}>
              {fullName}
            </span>
          </LinkWrapper>
          {badge}
        </div>
        {showEmail && email && (
          <div
            className={twMerge("text-text-secondary leading-normal truncate", sizeConfig.secondaryTextSize)}
            data-pii-type={piiEntity ? "email" : undefined}
            data-pii-entity={piiEntity}
          >
            {email}
          </div>
        )}
      </div>
    </div>
  );
}
